import { UploadCloud, SlidersHorizontal, CircleCheck, CircleX } from 'lucide-react';

import Container from '../../Main/sections/Container';
import Reveal from '../../../components/Reveal';
import { useScrollProgress } from '../../../hooks/useScrollProgress';

const STEPS = [
  {
    n: '1',
    title: 'Upload or select your material',
    body: "Choose from notes you've already uploaded, or drop in a new PDF, slide deck, or video. The quiz is built from your exact content — no generic questions.",
  },
  {
    n: '2',
    title: 'Customise your quiz',
    body: 'Pick the question type, difficulty level, number of questions, and which topics to focus on. Or let School Hack decide automatically.',
  },
  {
    n: '3',
    title: 'Take the quiz & see instant results',
    body: 'Get feedback on every answer, a full explanation of why you were wrong, and a detailed score breakdown. Retry weaker topics with one click.',
  },
];

const N = STEPS.length;

/* ---------- right-side visuals ---------- */

const UploadVisual = () => (
  <div className="card-dark rounded-2xl p-6">
    <div className="mb-4 flex items-center gap-2 text-white">
      <UploadCloud size={18} className="text-accent" />
      <span className="text-sm font-semibold">Upload Your Material</span>
    </div>
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-white/15 bg-white/[0.02] py-12 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/12 text-accent">
        <UploadCloud size={22} />
      </span>
      <p className="text-sm font-medium text-white">Drop your file here or paste a link</p>
      <p className="max-w-xs text-[0.75rem] text-white/40">
        Supports PDF, PPTX, DOCX, MP4, YouTube URLs and plain text
      </p>
    </div>
  </div>
);

const CustomiseVisual = () => (
  <div className="card-dark rounded-2xl p-6">
    <div className="mb-4 flex items-center gap-2 text-white">
      <SlidersHorizontal size={18} className="text-accent" />
      <span className="text-sm font-semibold">Customise your quiz</span>
    </div>
    <div className="flex flex-col gap-4">
      <div>
        <span className="text-[0.72rem] uppercase tracking-wide text-white/40">Question type</span>
        <div className="mt-2 flex flex-wrap gap-2">
          {['Multiple Choice', 'True / False', 'Short Answer', 'Fill Blank'].map((q, i) => (
            <span
              key={q}
              className={`rounded-full px-3 py-1.5 text-[0.72rem] font-medium ${
                i === 0
                  ? 'bg-accent/15 text-accent ring-1 ring-accent/40'
                  : 'bg-white/5 text-white/50'
              }`}
            >
              {q}
            </span>
          ))}
        </div>
      </div>
      <div>
        <span className="text-[0.72rem] uppercase tracking-wide text-white/40">Difficulty</span>
        <div className="mt-2 flex gap-2">
          {['Easy', 'Medium', 'Hard'].map((d, i) => (
            <span
              key={d}
              className={`flex-1 rounded-lg py-2 text-center text-[0.72rem] font-medium ${
                i === 1 ? 'bg-accent/15 text-accent ring-1 ring-accent/40' : 'bg-white/5 text-white/50'
              }`}
            >
              {d}
            </span>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between text-[0.72rem]">
          <span className="uppercase tracking-wide text-white/40">Questions</span>
          <span className="font-semibold text-accent">10</span>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
          <div className="h-full w-[55%] rounded-full bg-gradient-to-r from-accent to-accentDark" />
        </div>
      </div>
    </div>
  </div>
);

const ResultsVisual = () => (
  <div className="card-dark rounded-2xl p-6">
    <div className="mb-4 flex items-center gap-3">
      <span className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-accent text-sm font-bold text-accent">
        6/10
      </span>
      <div>
        <div className="text-sm font-semibold text-white">Your overall score</div>
        <div className="text-[0.75rem] text-white/45">Better luck next time!</div>
      </div>
    </div>
    <div className="rounded-xl border border-white/8 bg-white/[0.03] p-3.5">
      <p className="text-[0.78rem] font-medium text-white">
        What is the process by which plants create food?
      </p>
      <div className="mt-2 flex items-center gap-2 rounded-lg border border-[#f87171]/50 bg-[#f87171]/10 px-3 py-2 text-[0.72rem] text-white/85">
        <CircleX size={14} className="text-[#f87171]" /> Fermentation
      </div>
      <div className="mt-2 flex items-center gap-2 rounded-lg border border-accent/50 bg-accent/10 px-3 py-2 text-[0.72rem] text-white/85">
        <CircleCheck size={14} className="text-accent" /> Photosynthesis
      </div>
    </div>
    <div className="mt-3 rounded-xl border border-white/8 bg-white/[0.03] p-3.5">
      <p className="text-[0.78rem] font-medium text-white">Who wrote Romeo and Juliet?</p>
      <div className="mt-2 flex items-center gap-2 rounded-lg border border-accent/50 bg-accent/10 px-3 py-2 text-[0.72rem] text-white/85">
        <CircleCheck size={14} className="text-accent" /> William Shakespeare
      </div>
    </div>
  </div>
);

const VISUALS = [UploadVisual, CustomiseVisual, ResultsVisual];

const Header = () => (
  <div className="text-center">
    <span className="eyebrow">How It Works</span>
    <h2 className="font-heading mx-auto mt-5 max-w-3xl text-[2rem] font-bold leading-[1.1] sm:text-[2.5rem] lg:text-[2.9rem]">
      <span className="head-fade">From Your Notes To </span>
      <span className="text-accent-grad">A Full Quiz In Seconds</span>
    </h2>
    <p className="mx-auto mt-4 max-w-xl text-[0.95rem] text-white/55">
      Upload once. Quiz yourself endlessly. School Hack handles question
      generation so you spend time learning, not writing questions.
    </p>
  </div>
);

const StepRow = ({ step, isActive, fill, onClick }) => (
  <div className="flex gap-4" style={{ opacity: isActive ? 1 : 0.55, transition: 'opacity 0.4s ease' }}>
    <div className="relative mt-1 w-[3px] shrink-0 rounded-full bg-white/10">
      <span
        className="absolute left-0 top-0 w-full rounded-full bg-accent"
        style={{ height: `${fill * 100}%`, transition: 'height 0.1s linear' }}
      />
    </div>
    <button onClick={onClick} className="text-left">
      <div className="flex items-center gap-3">
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[0.75rem] font-semibold transition-colors ${
            isActive ? 'border-accent bg-accent/10 text-accent' : 'border-white/20 text-white/50'
          }`}
        >
          {step.n}
        </span>
        <span
          className={`font-heading text-[1.05rem] font-semibold transition-colors md:text-[1.2rem] ${
            isActive ? 'text-accent' : 'text-white'
          }`}
        >
          {step.title}
        </span>
      </div>
      <p className="mt-2 pl-[2.5rem] text-[0.88rem] leading-relaxed text-white/55">{step.body}</p>
    </button>
  </div>
);

const QuizHowItWorks = () => {
  const { ref, progress } = useScrollProgress();
  const scaled = progress * N;
  const index = Math.min(N - 1, Math.floor(scaled));

  return (
    <section ref={ref} className="relative pb-36 lg:h-[340vh]">
      {/* desktop pinned */}
      <div className="sticky top-0 hidden h-screen items-center overflow-hidden lg:flex">
        <div className="glow left-1/2 top-0 h-[420px] w-[700px] -translate-x-1/2 bg-accent/8" />
        <Container className="relative w-full">
          <Header />
          <div className="mt-12 grid grid-cols-[1fr_1fr] items-center gap-14">
            <div className="flex flex-col gap-9">
              {STEPS.map((s, i) => {
                const fill = i < index ? 1 : i === index ? Math.min(1, Math.max(0, scaled - i)) : 0;
                return (
                  <StepRow
                    key={s.n}
                    step={s}
                    isActive={i === index}
                    fill={fill}
                    onClick={() => {
                      const el = ref.current;
                      if (!el) return;
                      const distance = el.offsetHeight - window.innerHeight;
                      window.scrollTo({
                        top: el.offsetTop + ((i + 0.5) / N) * distance,
                        behavior: 'smooth',
                      });
                    }}
                  />
                );
              })}
            </div>
            <div className="relative h-[320px]">
              {VISUALS.map((V, i) => (
                <div
                  key={i}
                  className="absolute inset-0 transition-all duration-500 ease-out"
                  style={{
                    opacity: i === index ? 1 : 0,
                    transform: i === index ? 'translateY(0) scale(1)' : 'translateY(14px) scale(0.98)',
                    pointerEvents: i === index ? 'auto' : 'none',
                  }}
                >
                  <V />
                </div>
              ))}
            </div>
          </div>
        </Container>
      </div>

      {/* mobile stacked */}
      <div className="lg:hidden">
        <Container className="py-16">
          <Reveal>
            <Header />
          </Reveal>
          <div className="mt-10 flex flex-col gap-10">
            {STEPS.map((s, i) => {
              const V = VISUALS[i];
              return (
                <Reveal key={s.n} delay={40}>
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full border border-accent bg-accent/10 text-[0.8rem] font-semibold text-accent">
                      {s.n}
                    </span>
                    <span className="font-heading text-[1.15rem] font-semibold text-accent">
                      {s.title}
                    </span>
                  </div>
                  <p className="mt-2 pl-11 text-[0.9rem] leading-relaxed text-white/60">{s.body}</p>
                  <div className="mt-5">
                    <V />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </div>
    </section>
  );
};

export default QuizHowItWorks;
